import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import CommonModal from "../common11/commonModals/CommonModal";
import SearchDropdown from "../common11/formInputs/SearchDropdown";
import InputAreaField from "../common11/formInputs/InputAreaField";

// Sample options – replace with API when available
const DEPARTMENT_OPTIONS = [
  { id: 1, label: "General Medicine", value: "gm" },
  { id: 2, label: "General Surgery", value: "gs" },
  { id: 3, label: "Cardiology", value: "cardiology" },
  { id: 4, label: "Orthopedics", value: "ortho" },
];

const DOCTOR_OPTIONS = [
  { id: 1, label: "Dr Tukaram Aute", value: "tukaram" },
  { id: 2, label: "Dr Mahesh Mandora", value: "mahesh" },
  { id: 3, label: "Dr Ajay Oswal", value: "ajay" },
];

const ProceedToAdmissionModal = ({ isOpen, onClose, patientInfo = {} }) => {
  const name = patientInfo?.name ?? "—";
  const uhid = patientInfo?.uhid ?? "—";
  const age = patientInfo?.age ?? patientInfo?.ageGender ?? "—";
  const gender = patientInfo?.gender ?? "—";

  const methods = useForm({
    defaultValues: {
      department: null,
      doctor: null,
      reason: "",
    },
  });

  const { reset } = methods;

  const handleSave = () => {
    // TODO: API call to save proceed-to-admission details
    onClose();
  };

  const handleReset = () => {
    reset({
      department: null,
      doctor: null,
      reason: "",
    });
  };

  return (
    <CommonModal
      isOpen={isOpen}
      onClose={onClose}
      title="Proceed To Admission"
      size="lg"
      showCloseButton
      disableBackdropClose={true}
      DialogContent={
        <FormProvider {...methods}>
          <div className="space-y-4">
            {/* 1. Basic patient info – same pattern as Refer Patient */}
            <div className="bg-gray-100 rounded-lg p-3 border border-gray-200 shadow-sm flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden">
                    {name && name !== "—" ? (
                      <span className="text-lg font-semibold text-gray-600">
                        {String(name).charAt(0).toUpperCase()}
                      </span>
                    ) : (
                      <span className="text-lg font-semibold text-gray-600">?</span>
                    )}
                  </div>
                </div>
                <div className="flex flex-1 flex-wrap items-center justify-between gap-x-6 gap-y-1 text-sm min-w-0">
                  <span className="text-[#217E90] font-semibold truncate">{name}</span>
                  <div className="flex items-baseline gap-3">
                    <span className="text-gray-500 text-xs shrink-0">Age</span>
                    <span className="text-[#217E90] font-semibold">: {age}</span>
                  </div>
                  <div className="flex items-baseline gap-3">
                    <span className="text-gray-500 text-xs shrink-0">Gender</span>
                    <span className="text-[#217E90] font-semibold">: {gender}</span>
                  </div>
                  <div className="flex items-baseline gap-3">
                    <span className="text-gray-500 text-xs shrink-0">UHID</span>
                    <span className="text-[#217E90] font-semibold">: {uhid}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 2. Listing placeholder – Department / Doctor / Reason */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto max-h-[200px] overflow-y-auto">
                <table className="w-full text-sm border-collapse">
                  <thead className="bg-[#F8FEFF] sticky top-0 z-10">
                    <tr>
                      <th className="border-b border-r px-3 py-2 text-left font-semibold text-[#217E90] whitespace-nowrap">
                        Department
                      </th>
                      <th className="border-b border-r px-3 py-2 text-left font-semibold text-[#217E90] whitespace-nowrap">
                        Doctor Name
                      </th>
                      <th className="border-b px-3 py-2 text-left font-semibold text-[#217E90] whitespace-nowrap">
                        Reason
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td colSpan={3} className="py-8 text-center text-gray-500">
                        No records yet
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* 3. Form fields – Department, Doctor, Reason For Admission */}
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <SearchDropdown
                  name="department"
                  control={methods.control}
                  label="Department"
                  dataArray={DEPARTMENT_OPTIONS}
                  isSearchable
                  isClearable
                  isFloatingLabel
                  fontSize="text-xs"
                  selectSize="h-8"
                  placeholder="Select department..."
                />
                <SearchDropdown
                  name="doctor"
                  control={methods.control}
                  label="Doctor"
                  dataArray={DOCTOR_OPTIONS}
                  isSearchable
                  isClearable
                  isFloatingLabel
                  fontSize="text-xs"
                  selectSize="h-8"
                  placeholder="Select doctor..."
                />
              </div>
              <InputAreaField
                name="reason"
                control={methods.control}
                label="Reason For Admission"
                minRows={3}
                fontSize="text-xs"
              />
            </div>
          </div>
        </FormProvider>
      }
      actions={[
        {
          onClick: handleReset,
          label: "Reset",
          variant: "danger-outline",
          size: "sm",
          type: "button",
          className: "flex-1 sm:flex-none px-3 md:px-5 text-xs md:text-sm",
        },
        {
          onClick: handleSave,
          label: "Save",
          variant: "primary-filled",
        },
      ]}
    />
  );
};

export default ProceedToAdmissionModal;
